import { useState } from 'react'
import { Loader2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { AgentThreadCarryMode } from '@/lib/api'

interface ThreadCreateButtonProps {
  disabled?: boolean
  onCreateThread: (carryMode: AgentThreadCarryMode) => Promise<void>
}

export function ThreadCreateButton({ disabled = false, onCreateThread }: ThreadCreateButtonProps) {
  const [open, setOpen] = useState(false)
  const [isWorking, setIsWorking] = useState(false)

  const handleCreate = async (carryMode: AgentThreadCarryMode) => {
    if (disabled || isWorking) return

    setIsWorking(true)
    try {
      await onCreateThread(carryMode)
      setOpen(false)
    } finally {
      setIsWorking(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          aria-label="Create new chat"
          disabled={disabled || isWorking}
        >
          {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2">
        <div className="px-2 pt-1 pb-2 border-b">
          <p className="text-sm font-medium">New chat</p>
          <p className="text-[11px] text-muted-foreground">Choose how to start this chat</p>
        </div>
        <div className="space-y-1 p-1">
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-8 justify-start text-xs"
            onClick={() => { void handleCreate('carry_summary') }}
            disabled={disabled || isWorking}
          >
            Carry forward context
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-8 justify-start text-xs"
            onClick={() => { void handleCreate('fresh') }}
            disabled={disabled || isWorking}
          >
            Start fresh
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
